import { ApiPromise, WsProvider } from '@polkadot/api';

const STASH_TO_IDENTITY = {
    '14bGpFntaPUgoSNdXeU99ddPH8RvHfZRic1dCcQBwnNuJwwr': 'digitalghost.xyz/dot01',
    '13jagnVH4vS6a5XD1xixGA4zUxgjkZ9h8dbQDVCDZYR6rS9m': 'digitalghost.xyz/dot02',
    'FkqSqeRVr4VceTBKFXzwdSAjmB1QuhhVTdN1CXhRFvtPRZa': 'digitalghost.xyz/ksm01',
    'HnXarAhRKBsFfiiYJNZZf24BurrH8WkvWMguVt8dw5sxFah': 'digitalghost.xyz/ksm02',
    'GUTWXeDJ9CDvHKo6hmKQtgsppXgvxyygUWJBak4DRqa3pyB': 'digitalghost.xyz/ksm03',
    'J7LxQM11jrFNLoB7ucfLGE814Jg3emnY2uJW3Hii1cv136G': 'digitalghost.xyz/ksm04',
    'HEYzAi8mdAFmJeP1wCP32giBKpA7yAAPqPCqR6qNzz7heEM': 'digitalghost.xyz/ksm05',
};
const POLKADOT_ENDPOINT = 'wss://rpc-asset-hub-polkadot.luckyfriday.io';
const KUSAMA_ENDPOINT = 'wss://rpc-asset-hub-kusama.luckyfriday.io';
const BACKEND_API = 'https://api.digitalghost.xyz';

const network = (stash) => stash.startsWith('1') ? 'polkadot' : 'kusama';
const to_human_commission = (commission) => `${(+commission / 1e7).toFixed(2)}%`;
const to_human_currency = (amount, symbol) => {
  const units = ['', 'K', 'M', 'B', 'T'];
  const divider = symbol === 'DOT' ? 1e10 : 1e12;
  const a = amount / divider;
  if (a < 10000) {
    return a.toFixed(0);
  }
  for (let i = 0, a = amount / divider; i < units.length; i++, a /= 1000) {
    if (a < 1000) {
      return `${a.toFixed(2)}${units[i]}`;
    }
  }
};

async function lastPayoutTime(stash) {
    const endpoint = `${BACKEND_API}/assethub-${network(stash)}.webapi.subscan.io/api/v2/scan/account/reward_slash`;
    try {
        const res = await fetch(endpoint, {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify({
                address: stash,
                row: 1,
                page: 0,
                category: 'Reward',
                is_stash: true,
                claimed_filter: 'claimed',
            }),
        });
        const data = (await res.json()).data.list;
        if (!data) { return ''; }

        const timestamp = data[0].block_timestamp;
        const timeDiff = Date.now() / 1000 - timestamp;
        let rendered = '';
        if (timeDiff < 120) {
            rendered = `just now`;
        } else if (timeDiff < 3600 * 2) {
            const diffHours = Math.floor(timeDiff / 60);
            rendered = `${diffHours} minutes ago`;
        } else if (timeDiff < 3600 * 24 * 2) {
            const diffHours = Math.floor(timeDiff / 3600);
            rendered = `${diffHours} hours ago`;
        } else {
            const diffHours = Math.floor(timeDiff / 3600 / 24);
            rendered = `${diffHours} days ago`;
        }
        return rendered;
    } catch (err) {
        console.error('Payout', err);
        return '';
    }
}

export function validatorsPlaceholder() {
    const stashes = Object.keys(STASH_TO_IDENTITY);
    return stashes.map((stash) => ({
        endpoint: network(stash) === 'polkadot' ? POLKADOT_ENDPOINT : KUSAMA_ENDPOINT,
        stash,
        identity: STASH_TO_IDENTITY[stash],
        active: false,
        commission: '',
        nominators: 0,
        stake: 0,
        total: 0,
        stakeText: '',
        payout: '',
        recentEras: [],
    }));
}

async function getActiveEra(api) {
    return (await api.query.staking.activeEra()).unwrap().index;
}

async function fetchJson(url) {
    try {
        const res = await fetch(url);
        return await res.json();
    } catch {
        return {};
    }
}

export async function validatorsData() {
    console.time('API establishment');
    const [
        polkadotApi,
        kusamaApi,
    ] = await Promise.all([
        ApiPromise.create({ provider: new WsProvider(POLKADOT_ENDPOINT, 1000 * 5) }),
        ApiPromise.create({ provider: new WsProvider(KUSAMA_ENDPOINT, 1000 * 5) }),
    ]);
    console.timeEnd('API establishment');

    console.time('Fetching blockchain info');
    const [
        polkadotActiveEra,
        kusamaActiveEra,
    ] = await Promise.all([
        getActiveEra(polkadotApi),
        getActiveEra(kusamaApi),
    ]);
    console.timeEnd('Fetching blockchain info');

    console.time('Fetching validators info');
    const stashes = Object.keys(STASH_TO_IDENTITY);
    const validators = await Promise.all(stashes.map(async (stash) => {
        const networkId = network(stash);
        const [endpoint, api, activeEra, symbol] = networkId === 'polkadot'
            ? [POLKADOT_ENDPOINT, polkadotApi, polkadotActiveEra, 'DOT']
            : [KUSAMA_ENDPOINT, kusamaApi, kusamaActiveEra, 'KSM'];
        const [
            profile,
            gradeDetail,
            payout,
            stakerOverviews,
        ] = await Promise.all([
            fetchJson(`${BACKEND_API}/${networkId}-onet-api.turboflakes.io/api/v1/validators/${stash}/profile`),
            fetchJson(`${BACKEND_API}/${networkId}-onet-api.turboflakes.io/api/v1/validators/${stash}/grade?number_last_sessions=96`),
            lastPayoutTime(stash),
            Promise.all(Array.from({ length: 17 }, async (_, i) => {
                const era = activeEra - i;
                const stakerOverview = await api.query.staking.erasStakersOverview(era, stash);
                const staker = stakerOverview.unwrapOrDefault();
                const activeStake = +staker.total;
                const active = activeStake > 0;
                const nominators = +staker.nominatorCount;
                return {
                    era,
                    activeStake,
                    active,
                    nominators,
                };
            })),
        ]);

        function getIdentity() {
            if (!profile.identity) return STASH_TO_IDENTITY[stash];
            const { name, sub } = profile.identity;
            return name + (sub ? '/' + sub : '');
        }

        return {
            endpoint,
            stash,
            identity: getIdentity(),
            active: profile.is_active,
            commission: to_human_commission(profile.commission),
            nominators: profile.nominators_counter,
            grade: gradeDetail.grade,
            stake: profile.own_stake,
            total: profile.nominators_stake,
            stakeText: `${to_human_currency(stakerOverviews[0]?.activeStake || 0, symbol)} / ${to_human_currency(profile.nominators_stake, symbol)} ${symbol}`,
            payout: payout ? payout : '-',
            recentEras: stakerOverviews.slice(0).reverse().map((overview) => {
                overview.stake = `${to_human_currency(overview.activeStake, symbol)} ${symbol}`;
                return overview;
            }),
        };
    }));
    console.timeEnd('Fetching validators info');
    return validators;
}
